// ===========================================================
// AUDIT TRAIL COMPONENTS FOR PATIENT DETAIL PAGE
// ===========================================================
// Add these components to your PatientDetailPage.js

import React from 'react';

/**
 * Display audit info for medications assigned to patient
 */
export const MedicationAuditInfo = ({ medication }) => {
  if (!medication.assignedBy && !medication.assignedAt) return null;
  
  return (
    <div className="text-xs text-gray-500 mt-1">
      {medication.assignedBy && (
        <div>
          Assigned by: <span className="font-medium">{medication.assignedBy}</span>
        </div>
      )}
      {medication.assignedAt && (
        <div>
          on {new Date(medication.assignedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Display audit info for services assigned to patient
 */
export const ServiceAuditInfo = ({ service }) => {
  if (!service.assignedBy && !service.assignedAt) return null;
  
  return (
    <div className="text-xs text-gray-500 mt-1">
      {service.assignedBy && (
        <div>
          Assigned by: <span className="font-medium">{service.assignedBy}</span>
        </div>
      )}
      {service.assignedAt && (
        <div>
          on {new Date(service.assignedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Display audit info for protocols assigned to patient
 * Shows both assignment and update info
 */
export const ProtocolAuditInfo = ({ protocol }) => {
  const hasAssignedInfo = protocol.assignedBy || protocol.assignedAt;
  const hasUpdatedInfo = protocol.updatedBy || protocol.updatedAt;
  
  if (!hasAssignedInfo && !hasUpdatedInfo) return null;
  
  return (
    <div className="text-xs text-gray-500 mt-2 space-y-1 border-t pt-2">
      {/* Assignment Info */}
      {hasAssignedInfo && (
        <div>
          {protocol.assignedBy && (
            <div>
              Assigned by: <span className="font-medium">{protocol.assignedBy}</span>
            </div>
          )}
          {protocol.assignedAt && (
            <div className="ml-2">
              on {new Date(protocol.assignedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}
        </div>
      )}
      
      {/* Update Info - Only show if different from assigned info */}
      {hasUpdatedInfo && protocol.updatedBy !== protocol.assignedBy && (
        <div className="mt-1">
          {protocol.updatedBy && (
            <div>
              Updated by: <span className="font-medium">{protocol.updatedBy}</span>
            </div>
          )}
          {protocol.updatedAt && (
            <div className="ml-2">
              on {new Date(protocol.updatedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ===========================================================
// USAGE EXAMPLES
// ===========================================================

/**
 * Example 1: Medication Card with Audit Trail
 */
export const MedicationCardExample = ({ medication }) => {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="font-semibold text-lg">{medication.medicationName}</h3>
          <p className="text-gray-600">{medication.dosage} - {medication.frequency}</p>
          
          {/* ADD THIS COMPONENT */}
          <MedicationAuditInfo medication={medication} />
        </div>
        <div className="flex gap-2">
          <button className="text-spruce-600 hover:text-spruce-800">Edit</button>
          <button className="text-red-600 hover:text-red-800">Delete</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Example 2: Service Card with Audit Trail
 */
export const ServiceCardExample = ({ service }) => {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="font-semibold text-lg">{service.serviceName}</h3>
          <p className="text-gray-600">
            Next Due: {new Date(service.nextDueDate).toLocaleDateString()}
          </p>
          
          {/* ADD THIS COMPONENT */}
          <ServiceAuditInfo service={service} />
        </div>
        <div className="flex gap-2">
          <button className="text-spruce-600 hover:text-spruce-800">Edit</button>
          <button className="text-red-600 hover:text-red-800">Delete</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Example 3: Protocol Card with Audit Trail
 */
export const ProtocolCardExample = ({ protocol }) => {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="font-semibold text-lg">{protocol.protocolName}</h3>
          <p className="text-gray-600">
            Status: <span className="capitalize">{protocol.status}</span>
          </p>
          <p className="text-gray-600">
            Start Date: {new Date(protocol.startDate).toLocaleDateString()}
          </p>
          {protocol.currentPhase && (
            <p className="text-gray-600">Phase: {protocol.currentPhase}</p>
          )}
          
          {/* ADD THIS COMPONENT */}
          <ProtocolAuditInfo protocol={protocol} />
        </div>
        <div className="flex gap-2">
          <button className="text-spruce-600 hover:text-spruce-800">Edit</button>
          <button className="text-red-600 hover:text-red-800">Delete</button>
        </div>
      </div>
    </div>
  );
};

// ===========================================================
// ALTERNATIVE: Compact Badge Style
// ===========================================================

/**
 * Compact audit badge for table rows or compact layouts
 */
export const CompactAuditBadge = ({ assignedBy, assignedAt, updatedBy, updatedAt, type = 'assigned' }) => {
  const person = type === 'assigned' ? assignedBy : updatedBy;
  const date = type === 'assigned' ? assignedAt : updatedAt;
  
  if (!person && !date) return null;
  
  return (
    <div className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 rounded px-2 py-1">
      <span className="text-gray-400">{type === 'assigned' ? '📌' : '✏️'}</span>
      {person && <span className="font-medium">{person}</span>}
      {date && (
        <span>
          {new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })}
        </span>
      )}
    </div>
  );
};

/**
 * Example: Compact table row
 */
export const CompactTableRowExample = ({ medication }) => {
  return (
    <tr>
      <td className="px-4 py-2">{medication.medicationName}</td>
      <td className="px-4 py-2">{medication.dosage}</td>
      <td className="px-4 py-2">{medication.frequency}</td>
      <td className="px-4 py-2">
        <CompactAuditBadge 
          assignedBy={medication.assignedBy}
          assignedAt={medication.assignedAt}
          type="assigned"
        />
      </td>
      <td className="px-4 py-2">
        {/* Action buttons */}
      </td>
    </tr>
  );
};